import { usePortFetch, usePortsGetList } from '@ais-ui/api';
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
  ScrollArea,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Port } from '@ais-ui/api-spec/portserver';
import { useInfiniteScroll } from '@ais-ui/custom-hooks';
import { useLocalization } from '@ais-ui/locale-management';

export interface PortMultiSelectProps {
  value: string[];
  onChange: (value: string[] | undefined) => void;
  allowedGeometryTypes?: string[];
  'aria-invalid'?: React.ComponentProps<'button'>['aria-invalid'];
  'aria-describedby'?: string;
}

export function PortMultiSelect({
  value,
  onChange,
  ...props
}: PortMultiSelectProps) {
  const { messages } = useLocalization();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const limit = 50;
  const { viewportRef, offset, setOffset } = useInfiniteScroll(limit);
  const portFetch = usePortFetch();
  const { data, isInitialLoading } = usePortsGetList(search, offset, limit);
  const [ports, setPorts] = useState<Port[]>([]);
  const initialFetchDone = useRef(false);

  useEffect(() => {
    if (offset === 0) {
      setPorts(data?.records || []);
      return;
    }

    setPorts((prevPorts) => {
      const portMap = new Map(prevPorts.map((port) => [port.id, port]));

      // Add new ports from data.records
      (data?.records || []).forEach((port) => {
        if (port.id) {
          portMap.set(port.id, port);
        }
      });

      return Array.from(portMap.values());
    });
  }, [data, offset]);

  useEffect(() => {
    if (!value?.length || initialFetchDone.current) return;

    const uniquePortIds = new Set(ports.map((port: Port) => port.id));
    const missingPortIds = value.filter(
      (portId) => !uniquePortIds.has(Number(portId)),
    );

    if (missingPortIds.length === 0) {
      initialFetchDone.current = true;
      return;
    }

    const fetchPortsSequentially = async (portIds: string[]) => {
      const newPorts: Port[] = [];
      for (const portId of portIds) {
        const port = await portFetch(Number(portId));
        if (port) newPorts.push(port);
      }
      return newPorts;
    };

    fetchPortsSequentially(missingPortIds).then((validPorts) => {
      if (validPorts.length > 0) {
        setPorts((prevPorts) => {
          const portMap = new Map(prevPorts.map((port) => [port.id, port]));
          validPorts.forEach((port) => {
            if (port.id) portMap.set(port.id, port);
          });
          return Array.from(portMap.values());
        });
      }
      initialFetchDone.current = true;
    });
  }, [value, ports, portFetch]);

  const handleUnselect = useCallback(
    (deselectedPortId: string) => {
      onChange(value?.filter((portId) => portId !== deselectedPortId) || []);
    },
    [value, onChange],
  );

  return (
    <Popover
      open={open}
      onOpenChange={() => {
        setOpen(!open);
        setOffset(0);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between pr-3 hover:bg-card gap-1 text-base shadow-xs font-normal md:text-sm aria-invalid:shadow-destructive-sm"
          disabled={isInitialLoading}
          {...props}
        >
          {value && value.length > 0 ? (
            value.map((portId) => {
              const port = ports.find((p) => p.id?.toString() === portId);
              return (
                <Badge key={portId} variant="secondary">
                  {port?.name}
                  <button
                    className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUnselect(portId);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={() => handleUnselect(portId)}
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </Badge>
              );
            })
          ) : (
            <span>{messages.portMultiSelectText}</span>
          )}
          <span className="flex-1"></span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContentNoPortal align="start" className="w-full p-0 h-[300px]">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={messages.portSelectSearchText}
            onValueChange={(value) => {
              setSearch(value);
              setOffset(0);
            }}
            value={search}
          />
          <CommandEmpty>{messages.portSelectNoResults}</CommandEmpty>
          <ScrollArea
            infiniteScrollRef={viewportRef}
            className="flex-grow p-0 overflow-y-auto border rounded-lg"
          >
            {ports?.map((port: Port) => {
              const stringPortId = port.id ? port.id.toString() : '';
              return (
                <CommandItem
                  key={port.id}
                  value={stringPortId}
                  onSelect={() => {
                    if (stringPortId) {
                      const newValue = value?.includes(stringPortId)
                        ? value.filter((id) => id !== stringPortId)
                        : [...(value || []), stringPortId];

                      onChange(newValue.length > 0 ? newValue : undefined);
                      setOpen(false);
                    }
                  }}
                >
                  <div className="flex justify-between w-full">
                    <div className="flex">{port.name}</div>
                    <Check
                      className={cn(
                        'mr-1 size-4',
                        value.includes(stringPortId)
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                  </div>
                </CommandItem>
              );
            })}
          </ScrollArea>
        </Command>
      </PopoverContentNoPortal>
    </Popover>
  );
}
